/**
 * PALO back-office design tokens — extracted verbatim from Christian's redesign
 * prototype (#2132). Single source of truth for the back-office look. The shop
 * stays on theme-next and is not affected by anything here.
 */
export const tokens = {
    color: {
        // Values from the colour concept (GF 15.10.2026, #2290): a calm neutral
        // ground, one accent for what can be operated, status colours tuned to
        // each other. No pure white or black on large areas or text.
        // Ground & chrome
        canvas: '#F6F7F9',
        surface: '#FFFFFF',
        surfaceAlt: '#FAFBFC',
        /** One step below surfaceAlt — the hover of a row in a dense list. */
        surface2: '#F7F9FB',
        chrome: '#1F2A37',
        chrome2: '#263241',
        chromeLine: '#364253',
        /** Icons and secondary text on the chrome. */
        chromeInk: '#9AA6B6',
        // Text
        ink: '#1E2733',
        ink2: '#5B6675',
        ink3: '#8A939C',
        inkInvert: '#FFFFFF',
        // Accent (brand)
        accent: '#2563A8',
        /** The accent as text on accentSoft, and the hover of the accent. */
        accentHi: '#1D4F86',
        accentSoft: '#E8F0FA',
        accentLine: '#B8CDE6',
        // Semantics (kept separate from the accent)
        ok: '#22663F',
        okSoft: '#E6F4EC',
        okInk: '#22663F',
        /** Amber: please mind. The one colour of every notice. */
        warn: '#B7791F',
        warnSoft: '#FDF3E1',
        warnInk: '#7A4E0F',
        /** Marks a missing entry on white: warn itself is too faint there (#2335). */
        warnMark: '#B87A00',
        /** The text of a notice, under its amber title. */
        warnText: '#5C3A0B',
        /** Errors and what cannot be undone: a field's message, delete. */
        crit: '#B3261E',
        critSoft: '#FBE9E7',
        /** The text of a negative status, on critSoft. */
        critInk: '#9B2C20',
        /** The edge of a field whose input is wrong. */
        critField: '#C0392B',
        neutral: '#6F7377',
        neutralSoft: '#EEF1F5',
        neutralInk: '#4A5563',
        /** The text of a running status, on white with a field edge. */
        runningInk: '#3F4A58',
        // The internal remark has a colour of its own in the prototype: it is
        // neither a state nor a warning, and must not read as either.
        internal: '#6B3FA0',
        internalSoft: '#F0EAF8',
        internalLine: '#C9B3E3',
        // Kinds of document. Status already takes grey, blue, amber, red and
        // green, so the kind of a document stays clear of all five.
        kindInquiry: '#3F3A9E',
        kindInquirySoft: '#EDECFA',
        kindOrder: '#0E6A6E',
        kindOrderSoft: '#E1F2F2',
        kindInvoice: '#7A5A14',
        kindInvoiceSoft: '#F6EEDB',
        kindPriceInfo: '#7E3A68',
        kindPriceInfoSoft: '#F5E8F1',
        kindBonus: '#4F5C17',
        kindBonusSoft: '#EDF1DC',
        // Lines
        line: '#DDE2E8',
        lineSoft: '#EEF1F4',
        lineHard: '#CFD5DC',
        /**
         * The edge of a field that can be typed into. lineHard drew it at about
         * 1.5 : 1 against the canvas and a field read as empty page; this is
         * 3 : 1 (#2335). Tables and cards stay on line and lineHard.
         */
        field: '#8A95A5',
        /** The band that says this is not the live system (#2335). */
        testBand: '#7A4E0F',
        // Tinted borders, for the badges and dots that sit on a soft ground
        okLine: '#A8D5BA',
        warnLine: '#E9C98F',
        critLine: '#EDB8B1',
    },
    size: {
        /**
         * A block control — a drop zone, a remark, an editor — is this tall.
         *
         * They sit next to each other in the two-column form rows, and each one
         * used to bring its own height: a drop zone 144 from the screen design,
         * a two-row remark 86. The row then read as broken rather than as two
         * fields.
         */
        blockH: 144,
        rowH: 38,
        rowHHead: 34,
        railW: 56,
        navW: 212,
        topH: 48,
    },
    radius: {
        default: 6,
        small: 4,
    },
    shadow: {
        s1: '0 1px 2px rgba(26,32,39,.08), 0 1px 3px rgba(26,32,39,.04)',
        s2: '0 4px 12px rgba(26,32,39,.10), 0 1px 3px rgba(26,32,39,.06)',
        s3: '0 16px 48px rgba(26,32,39,.20), 0 4px 12px rgba(26,32,39,.10)',
    },
    font: {
        sans: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    },
} as const;

export type PaloTokens = typeof tokens;
